import type { Popover } from '@radix-ui/themes';
import { Heading, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import HubertTabs from 'components/HubertUI/HubertTabs';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TMatrixSliderEvaluationWeighted, TSliderEvaluationConditional } from 'types/evaluation.types';
import { ESliderEvaluationType, isConditionalEvaluation, isWeightedEvaluation } from 'types/evaluation.types';
import { type TFormSlider } from '@hubertai/screening-question-types';
import SliderEvaluationConditional from './SliderEvaluationConditional';
import SliderEvaluationTriggerButton from './SliderEvaluationTriggerButton';
import SliderEvaluationWeighted from './SliderEvaluationWeighted';

interface Props extends ComponentProps<typeof Popover.Root> {
  allowedTabs?: (ESliderEvaluationType.Weighted | ESliderEvaluationType.Conditional)[];
  form: TFormSlider;
  evaluation: TSliderEvaluationConditional[] | TMatrixSliderEvaluationWeighted;
  onEvaluationChange: (
    e:
      | {
          weight: number;
          policy: 'basic';
        }
      | TSliderEvaluationConditional[]
      | object,
  ) => void;
}

const SliderEvaluation = ({
  allowedTabs = [ESliderEvaluationType.Weighted, ESliderEvaluationType.Conditional],
  form,
  evaluation,
  onEvaluationChange,
  ...props
}: Props) => {
  // console.log('SliderEvaluation', templateId, nodeId, evaluation)
  const { t } = useTranslation('Templates');

  const tabs = useMemo(
    () => [
      { id: ESliderEvaluationType.Weighted, name: t('evaluationPopover.matrixTabs.weighted'), description: '' },
      { id: ESliderEvaluationType.Conditional, name: t('evaluationPopover.matrixTabs.conditional'), description: '' },
      { id: '', name: t('evaluationPopover.matrixTabs.notSet'), description: '' },
    ],
    [t],
  );

  const [originalEvaluationType, setOriginalEvaluationType] = useState<ESliderEvaluationType | ''>('');
  const [_, setOriginalEvaluation] = useState<TSliderEvaluationConditional[] | TMatrixSliderEvaluationWeighted>(
    evaluation,
  );

  const [evaluationType, setEvaluationType] = useState<ESliderEvaluationType | ''>('');
  const [evaluationConditional, setEvaluationConditional] = useState<TSliderEvaluationConditional[]>([]);

  const [evaluationWeighted, setEvaluationWeighted] = useState<TMatrixSliderEvaluationWeighted>({
    weight: 0,
    policy: 'basic',
  });

  useEffect(() => {
    setOriginalEvaluation(evaluation);

    if (isWeightedEvaluation(evaluation)) {
      setOriginalEvaluationType(ESliderEvaluationType.Weighted);

      setEvaluationType(ESliderEvaluationType.Weighted);
      setEvaluationConditional([]);
      setEvaluationWeighted(evaluation);
    } else if (isConditionalEvaluation(evaluation)) {
      setOriginalEvaluationType(ESliderEvaluationType.Conditional);

      setEvaluationType(ESliderEvaluationType.Conditional);
      setEvaluationConditional(evaluation);
      setEvaluationWeighted({
        weight: 0,
        policy: 'basic',
      });
    } else {
      setEvaluationType('');
      setOriginalEvaluationType('');
    }
  }, [evaluation]);

  const portalContent = document.getElementById('main-content') as HTMLElement;
  return (
    <HubertPopover {...props} modal={true}>
      <HubertPopover.Trigger>
        <SliderEvaluationTriggerButton evaluationType={originalEvaluationType} />
      </HubertPopover.Trigger>

      <HubertPopover.Content side="left" portalContent={true} portalElement={portalContent}>
        <HubertTabs
          defaultValue={evaluationType}
          value={evaluationType}
          onValueChange={(e: any) => {
            setEvaluationType(e);
          }}
          activationMode="manual"
        >
          <HubertTabs.List>
            {tabs.map((tab) => {
              if (tab.id === '' || allowedTabs.includes(tab.id as ESliderEvaluationType)) {
                return (
                  <HubertTabs.Trigger key={`slider-evaluation-popover-tab-trigger-${tab.id}`} value={tab.id}>
                    {tab.name}
                  </HubertTabs.Trigger>
                );
              }

              return null;
            })}
          </HubertTabs.List>

          {tabs.map((tab) => {
            return (
              <HubertTabs.Content
                value={tab.id}
                key={`slider-evaluation-popover-tab-content-${tab.id}`}
                className={clsx('min-h-[200px]', 'min-w-[450px]')}
              >
                {(() => {
                  if (tab.id === ESliderEvaluationType.Conditional) {
                    return (
                      <SliderEvaluationConditional
                        // @ts-ignore
                        form={form}
                        evaluation={evaluationConditional}
                        onEvaluationChange={(updatedEvaluation: any) => {
                          console.log('onEvaluationChange', updatedEvaluation);
                          setEvaluationConditional(updatedEvaluation);
                        }}
                      />
                    );
                  } else if (tab.id === ESliderEvaluationType.Weighted) {
                    return (
                      <SliderEvaluationWeighted
                        evaluation={evaluationWeighted}
                        onEvaluationChange={(x: number[]) => {
                          console.log('onEvaluationChange', x);
                          setEvaluationWeighted({
                            ...evaluationWeighted,
                            weight: x[0],
                          });
                        }}
                      />
                    );
                  } else {
                    return (
                      <HubertTabs.Content
                        value={tab.id}
                        key={`evaluation-popover-tab-content-${tab.id}`}
                        className="min-h-[200px] min-w-[300px]"
                      >
                        <Heading size="3" weight={'medium'}>
                          {tab.name}
                        </Heading>
                        <Text as="p" size="1">
                          {t('evaluationPopover.noEvaluationSet')}
                        </Text>
                      </HubertTabs.Content>
                    );
                  }
                })()}
              </HubertTabs.Content>
            );
          })}

          <div className={clsx('flew-row flex justify-between gap-2', 'hubert-vertical-center')}>
            {/* TODO(i18n): Category 4 — sentence split across JSX children with inline <Link>; restructure before extracting */}
            <span className="text-sm">
              Read about Hubert evaluations{' '}
              <Link to={'/evaluation'} target="_blank" className="text-info-500">
                here
              </Link>
              .
            </span>

            <div className={clsx('flew-row flex justify-between gap-2', 'hubert-vertical-center')}>
              <HubertPopover.Close asChild>
                <HubertButton
                  size="sm"
                  variant={'secondary'}
                  // disabled={!isEdited}
                >
                  {t('evaluationPopover.cancelButton')}
                </HubertButton>
              </HubertPopover.Close>

              <HubertPopover.Close asChild>
                <HubertButton
                  size="sm"
                  // disabled={!isEdited}
                  onClick={() => {
                    if (!onEvaluationChange) {
                      console.log('!onEvaluationChange');
                      return;
                    }

                    if (evaluationType === ESliderEvaluationType.Weighted) {
                      onEvaluationChange(evaluationWeighted);
                      return;
                    }

                    if (evaluationType === ESliderEvaluationType.Conditional) {
                      onEvaluationChange(evaluationConditional);
                      return;
                    }

                    onEvaluationChange({});
                    return;
                  }}
                >
                  {t('evaluationPopover.saveButton')}
                </HubertButton>
              </HubertPopover.Close>
            </div>
          </div>
        </HubertTabs>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default SliderEvaluation;
